import Vue from 'vue'
import Router from 'vue-router'
import { store } from '@/store'

/**
 * lazy loading pages
 * @see https://router.vuejs.org/en/advanced/lazy-loading.html
 */
const NotFound = () => import('@/pages/404')
const login = () => import('@/pages/login')
const signup = () => import('@/pages/signup')
const app = () => import('@/pages/app')

const helpIndex = () => import('@/pages/help/index')

const settingsIndex = () => import('@/pages/settings/index')
const settingsAccountEdit = () => import('@/pages/settings/account/edit')
const settingsNotificationEdit = () => import('@/pages/settings/notification/edit')
const settingsPayoutEdit = () => import('@/pages/settings/payout/edit')

const dashboardIndex = () => import('@/pages/dashboard/index')

const profileIndex = () => import('@/pages/profile/index')
const profileEdit = () => import('@/pages/profile/edit')

const productsIndex = () => import('@/pages/products/index')
const productsList = () => import('@/pages/products/list')
const productsEdit = () => import('@/pages/products/edit')

const rewardsIndex = () => import('@/pages/rewards/index')
const rewardsList = () => import('@/pages/rewards/list')
const rewardsEdit = () => import('@/pages/rewards/edit')

const postsIndex = () => import('@/pages/posts/index')
const postsList = () => import('@/pages/posts/list')
const postsEdit = () => import('@/pages/posts/edit')

const newslettersIndex = () => import('@/pages/newsletters/index')
const newslettersList = () => import('@/pages/newsletters/list')
const newslettersEdit = () => import('@/pages/newsletters/edit')

const tagsIndex = () => import('@/pages/tags/index')
const tagsList = () => import('@/pages/tags/list')

const pollsIndex = () => import('@/pages/polls/index')
const pollsList = () => import('@/pages/polls/list')

const purchasesIndex = () => import('@/pages/purchases/index')
const purchasesList = () => import('@/pages/purchases/list')
const purchasesEdit = () => import('@/pages/purchases/edit')

const ordersIndex = () => import('@/pages/orders/index')
const ordersList = () => import('@/pages/orders/list')

Vue.use(Router)

const router = new Router({
  mode: 'history',
  scrollBehavior(to, from, savedPosition) {
    return { x: 0, y: 0 }
  },
  routes: [
    {
      path: '*',
      component: NotFound,
      meta: { isPublic: true }
    },
    {
      path: '/',
      component: login,
      meta: { isPublic: true }
    },
    {
      path: '/login',
      name: 'login',
      component: login,
      meta: { isPublic: true }
    },
    {
      path: '/signup',
      name: 'signup',
      component: signup,
      meta: { isPublic: true }
    },
    {
      path: '/myprofile',
      component: app,
      children: [
        {
          path: '',
          redirect: 'dashboard'
        },
        {
          path: 'settings',
          component: settingsIndex,
          children: [
            {
              path: '',
              name: 'settingsIndex',
              redirect: 'account'
            },
            {
              path: 'account',
              name: 'accountEdit',
              component: settingsAccountEdit
            },
            {
              path: 'notification',
              name: 'notificationEdit',
              component: settingsNotificationEdit
            },
            {
              path: 'payout',
              name: 'payoutEdit',
              component: settingsPayoutEdit
            }
          ]
        },
        {
          path: 'help',
          name: 'helpIndex',
          component: helpIndex
        },
        {
          path: 'dashboard',
          name: 'dashboardIndex',
          component: dashboardIndex
        },
        {
          path: 'profile',
          component: profileIndex,
          children: [
            {
              path: '',
              name: 'profileEdit',
              component: profileEdit
            }
          ]
        },
        {
          path: 'products',
          component: productsIndex,
          children: [
            {
              path: '',
              name: 'productsList',
              redirect: {
                name: 'productsListStatus',
                params: { status: 'published' }
              }
            },
            {
              path: ':status',
              name: 'productsListStatus',
              component: productsList
            },
            {
              path: 'edit/:id',
              name: 'productsEdit',
              component: productsEdit
            },
            {
              path: 'add',
              name: 'productsAdd',
              component: productsEdit
            }
          ]
        },
        {
          path: 'rewards',
          component: rewardsIndex,
          children: [
            {
              path: '',
              name: 'rewardsList',
              redirect: {
                name: 'rewardsListStatus',
                params: { status: 'published' }
              }
            },
            {
              path: ':status',
              name: 'rewardsListStatus',
              component: rewardsList
            },
            {
              path: 'edit/:id',
              name: 'rewardsEdit',
              component: rewardsEdit
            },
            {
              path: 'add',
              name: 'rewardsAdd',
              component: rewardsEdit
            }
          ]
        },
        {
          path: 'posts',
          component: postsIndex,
          children: [
            {
              path: '',
              name: 'postsList',
              component: postsList
            },
            {
              path: ':status',
              name: 'postsListStatus',
              component: postsList
            },
            {
              path: 'edit/:id',
              name: 'postsEdit',
              component: postsEdit
            },
            {
              path: 'add',
              name: 'postsAdd',
              component: postsEdit
            },
            {
              path: 'tags',
              component: tagsIndex,
              children: [
                {
                  path: 'list',
                  name: 'tagsList',
                  component: tagsList
                }
              ]
            }
          ]
        },
        {
          path: 'newsletters',
          component: newslettersIndex,
          children: [
            {
              path: '',
              name: 'newslettersList',
              component: newslettersList
            },
            {
              path: ':status',
              name: 'newslettersListStatus',
              component: newslettersList
            },
            {
              path: 'edit/:id',
              name: 'newslettersEdit',
              component: newslettersEdit
            },
            {
              path: 'add',
              name: 'newslettersAdd',
              component: newslettersEdit
            }
          ]
        },
        {
          path: 'polls',
          component: pollsIndex,
          children: [
            {
              path: '',
              name: 'pollsList',
              component: pollsList
            }
          ]
        },
        {
          path: 'purchases',
          component: purchasesIndex,
          children: [
            {
              path: '',
              name: 'purchasesList',
              component: purchasesList
            },
            {
              path: 'edit/:purchaseType/:id',
              name: 'purchasesEdit',
              component: purchasesEdit
            }
          ]
        },
        {
          path: 'orders',
          component: ordersIndex,
          children: [
            {
              path: '',
              name: 'ordersList',
              component: ordersList
            }
          ]
        }
      ]
    }
  ]
})

router.beforeEach((to, from, next) => {
  if (
    to.name === 'dashboardIndex' &&
    store.getters.isAuthenticated &&
    !store.getters.isArtist
  ) {
    return next({ name: 'profileEdit' })
  }

  if (
    to.matched.some(
      record => record.meta.isPublic || store.getters.isAuthenticated
    )
  ) {
    return next()
  }
  next({ name: 'login', query: { redirect: to.fullPath } })
})

export default router



// WEBPACK FOOTER //
// ./src/router/index.js